import React, { useState, useRef, useCallback } from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { galleryImages } from '../data/gallery';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import './Gallery.css';

interface LightboxState {
  isOpen: boolean;
  currentIndex: number;
}

/**
 * Gallery page with filter capabilities and interactive lightbox
 * Features masonry layout, category filtering, and keyboard navigation
 */
export const Gallery: React.FC = () => {
  useDocumentTitle('Gallery');
  const [lightbox, setLightbox] = useState<LightboxState>({ isOpen: false, currentIndex: 0 });
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const gridRef = useScrollReveal({ threshold: 0.1 });
  const lightboxRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number>(0);

  // Extract unique categories
  const categories = ['All', ...Array.from(new Set(galleryImages.map(img => img.category)))];

  // Filter images based on selected category
  const filteredImages =
    activeCategory === 'All'
      ? galleryImages
      : galleryImages.filter(img => img.category === activeCategory);

  const getImage = (path: string) => {
    try {
      return require(`../assets/images/${path}`);
    } catch {
      return '';
    }
  };

  // Lightbox handlers
  const openLightbox = useCallback((index: number) => {
    setLightbox({ isOpen: true, currentIndex: index });
  }, []);

  const closeLightbox = useCallback(() => {
    setLightbox({ isOpen: false, currentIndex: 0 });
  }, []);

  const goToPrevious = useCallback(() => {
    setLightbox(prev => ({
      ...prev,
      currentIndex: (prev.currentIndex - 1 + filteredImages.length) % filteredImages.length,
    }));
  }, [filteredImages.length]);

  const goToNext = useCallback(() => {
    setLightbox(prev => ({
      ...prev,
      currentIndex: (prev.currentIndex + 1) % filteredImages.length,
    }));
  }, [filteredImages.length]);

  // Keyboard navigation
  React.useEffect(() => {
    if (!lightbox.isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          closeLightbox();
          break;
        case 'ArrowLeft':
          goToPrevious();
          break;
        case 'ArrowRight':
          goToNext();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightbox.isOpen, closeLightbox, goToPrevious, goToNext]);

  // Touch swipe handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) goToNext();
      else goToPrevious();
    }
  }, [goToNext, goToPrevious]);

  // Prevent body scroll when lightbox is open
  React.useEffect(() => {
    if (lightbox.isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [lightbox.isOpen]);

  const currentImage = filteredImages[lightbox.currentIndex];

  return (
    <div className="gallery-page page-enter">
      {/* Hero Section */}
      <section className="gallery-hero">
        <div className="gallery-hero__content">
          <h1 className="gallery-hero__title">Our Work</h1>
          <p className="gallery-hero__subtitle">A look at painting and finish work</p>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="gallery-filters">
        <div className="gallery-filters__container">
          <div className="gallery-filters__buttons">
            {categories.map(category => (
              <button
                key={category}
                className={`gallery-filter-button ${
                  activeCategory === category ? 'gallery-filter-button--active' : ''
                }`}
                onClick={() => setActiveCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Image Grid */}
      <section className="gallery-section">
        <div className="gallery-container">
          <div className="gallery-grid" ref={gridRef}>
            {filteredImages.map((image, index) => (
              <div
                key={`${image.src}-${index}`}
                className="gallery-item"
                style={{
                  animationDelay: `${index * 50}ms`,
                }}
              >
                <button
                  className="gallery-item__button"
                  onClick={() => openLightbox(index)}
                  aria-label={`Open ${image.alt}`}
                >
                  <img
                    src={getImage(image.src)}
                    alt={image.alt}
                    className="gallery-item__image"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="gallery-item__overlay" aria-hidden="true">
                    <svg className="gallery-item__zoom-icon" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="15 3 21 3 21 9" />
                      <polyline points="9 21 3 21 3 15" />
                      <line x1="21" y1="3" x2="14" y2="10" />
                      <line x1="3" y1="21" x2="10" y2="14" />
                    </svg>
                  </div>
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightbox.isOpen && currentImage && (
        <div
          ref={lightboxRef}
          className="gallery-lightbox"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label="Image viewer"
        >
          {/* Content */}
          <div
            className="gallery-lightbox__content"
            onClick={e => e.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {/* Close Button */}
            <button
              className="gallery-lightbox__close"
              onClick={closeLightbox}
              aria-label="Close lightbox"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            {/* Main Image */}
            <div className="gallery-lightbox__image-container">
              <img
                src={getImage(currentImage.src)}
                alt={currentImage.alt}
                className="gallery-lightbox__image"
              />
            </div>

            {/* Mobile navigation row — replaces side arrows on small screens */}
            <div className="gallery-lightbox__mobile-nav">
              <button
                className="gallery-lightbox__mobile-nav-btn"
                onClick={goToPrevious}
                aria-label="Previous image"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <span className="gallery-lightbox__mobile-counter">
                {lightbox.currentIndex + 1} / {filteredImages.length}
              </span>
              <button
                className="gallery-lightbox__mobile-nav-btn"
                onClick={goToNext}
                aria-label="Next image"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>

            {/* Navigation */}
            <div className="gallery-lightbox__nav">
              <button
                className="gallery-lightbox__nav-button gallery-lightbox__nav-button--prev"
                onClick={goToPrevious}
                aria-label="Previous image"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>

              <button
                className="gallery-lightbox__nav-button gallery-lightbox__nav-button--next"
                onClick={goToNext}
                aria-label="Next image"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>

            {/* Image Counter */}
            <div className="gallery-lightbox__counter">
              {lightbox.currentIndex + 1} / {filteredImages.length}
            </div>

            {/* Image Info */}
            <div className="gallery-lightbox__info">
              <p className="gallery-lightbox__alt">{currentImage.alt}</p>
              <p className="gallery-lightbox__category">{currentImage.category}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

Gallery.displayName = 'Gallery';
